import { useEffect, useRef, useState } from 'react';
import type { Template } from '@/models/template';
import { usePreviewTemplateMutation } from '@/state/slices/reportTemplates';

export const PREVIEW_DEBOUNCE_MS = 800;

/**
 * Renders the unsaved template against a study as the real PDF.
 *
 * Waits until editing pauses, then asks the backend for a fresh PDF. A newer
 * edit cancels the request in flight. The last good PDF stays visible while the
 * next one loads. A rename or the copy a save returns prints the same PDF, so
 * neither costs a render.
 */
/** The template without what doesn't print: its name and every timestamp. */
function printedContent({ name: _name, ...rest }: Template) {
  return JSON.stringify(rest, (key, value: unknown) =>
    key === 'createdAt' || key === 'updatedAt' ? undefined : value
  );
}

export function useTemplatePreview(
  template: Template,
  studyUuid: string | undefined
) {
  const [previewTemplate] = usePreviewTemplateMutation();
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isFetching, setIsFetching] = useState(false);

  const latestTemplate = useRef(template);
  useEffect(() => {
    latestTemplate.current = template;
  }, [template]);
  const content = printedContent(template);

  useEffect(() => {
    if (!studyUuid) return undefined;
    let active = true;
    let request: ReturnType<typeof previewTemplate> | null = null;

    const timer = setTimeout(() => {
      setIsFetching(true);
      request = previewTemplate({
        template: latestTemplate.current,
        studyUuid,
      });
      request
        .unwrap()
        .then((blob) => {
          if (!active) return;
          setPdfBlob(blob);
          setError(null);
        })
        .catch((err: unknown) => {
          if (active) setError(err);
        })
        .finally(() => {
          if (active) setIsFetching(false);
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
      request?.abort();
      setIsFetching(false);
    };
  }, [content, studyUuid, previewTemplate]);

  return {
    pdfBlob,
    isLoading: isFetching && !pdfBlob,
    isFetching,
    error,
  };
}
